import { gql } from '@apollo/client'
import { BrandJusIa, Card, Chip, Heading } from '@farol-ds/react'
import { Price } from '@jusbrasil-web/shared-price'
import classNames from 'classnames'

import { PlanFrequency } from '../../../constants'
import { BenefitsList } from '../../benefits-list'
import { CheckoutButton } from '../../checkout-button'
import { CheckoutButton_OfferLockCardFragment } from '../../checkout-button/__generated__/checkout-button.graphql'
import { OneClickButton } from '../../one-click-button'
import {
  JurisThreeOffersPlanCard_OfferLockCardFragment,
  JurisThreeOffersPlanCard_ViewerFragment,
} from './__generated__/juris-three-offers-switch-frequency-lock-plan-card.graphql'
import styles from './juris-three-offers-switch-frequency-lock-plan-card.module.scss'

export interface JurisThreeOffersSwitchFrequencyLockPlanCardProps {
  offerLockCard: JurisThreeOffersPlanCard_OfferLockCardFragment
  viewer: JurisThreeOffersPlanCard_ViewerFragment
  showCreditCardAndRemoveOneClick?: boolean
}

export function JurisThreeOffersSwitchFrequencyLockPlanCard(
  props: JurisThreeOffersSwitchFrequencyLockPlanCardProps
) {
  const { offerLockCard, showCreditCardAndRemoveOneClick, viewer } = props

  const { title, offer } = offerLockCard
  const {
    priceText,
    priceTitle,
    frequency,
    isJusAIOffer,
    priceAfterPromotionalPeriodText,
    frequencyPeriodText,
  } = offer

  const isYearlyPlan = frequency === PlanFrequency.YEARLY
  const isPromotional = !!priceAfterPromotionalPeriodText

  const kindSubmitButton = isJusAIOffer ? 'primary' : 'secondary'

  const offerLockCardWithoutCCToken = {
    ...offerLockCard,
    creditCard: {
      ...offerLockCard.creditCard,
      ccToken: null,
    } as CheckoutButton_OfferLockCardFragment['creditCard'],
  }

  return (
    <div className={styles.jurisThreeOffersPlanCard}>
      {isJusAIOffer && (
        <Chip size="md" kind="secondary" className={styles.jurisThreeOffersPlanCardChipRecommended}>
          Recomendado
        </Chip>
      )}
      <Card
        className={classNames(
          styles.jurisThreeOffersPlanCardCard,
          isJusAIOffer && styles.jurisThreeOffersPlanCardCardJusAI
        )}
        disablePadding
      >
        <div className={styles.jurisThreeOffersPlanCardCardBody}>
          <div className={styles.jurisThreeOffersPlanCardBody}>
            <div className={styles.jurisThreeOffersPlanCardMainSection}>
              {isJusAIOffer && (
                <BrandJusIa width={95} className={styles.jurisThreeOffersPlanCardJusAILogo} />
              )}

              <Heading size="md" asChild>
                <h4>{title}</h4>
              </Heading>

              <Price className={styles.jurisThreeOffersPlanCardPrice}>
                {priceTitle && (
                  <Price.Title>
                    <span dangerouslySetInnerHTML={{ __html: priceTitle }} />
                  </Price.Title>
                )}
                {isPromotional ? (
                  <>
                    <Price.Value>{priceText}</Price.Value>
                    <Price.Helptext>No {frequencyPeriodText}</Price.Helptext>
                  </>
                ) : (
                  <span>
                    <Price.Value>{priceText}</Price.Value>
                    {!isYearlyPlan && frequency && (
                      <Price.ValueFrequency>{frequency}</Price.ValueFrequency>
                    )}
                  </span>
                )}
              </Price>
            </div>

            {showCreditCardAndRemoveOneClick ? (
              <>
                <CheckoutButton kind={kindSubmitButton} offerLockCard={offerLockCard} />
                <CheckoutButton
                  kind={kindSubmitButton}
                  offerLockCard={offerLockCardWithoutCCToken}
                />
              </>
            ) : (
              <>
                <CheckoutButton kind={kindSubmitButton} offerLockCard={offerLockCard} />
                <OneClickButton
                  offerLockCard={offerLockCard}
                  viewer={viewer}
                  kind={kindSubmitButton}
                />
              </>
            )}

            <div className={styles.jurisThreeOffersPlanCardBenefitsSection}>
              <BenefitsList offerLockCard={offerLockCard} />
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

JurisThreeOffersSwitchFrequencyLockPlanCard.fragments = {
  offerLockCard: gql`
    fragment JurisThreeOffersPlanCard_offerLockCard on OfferLockCard {
      title
      subtitle
      offer {
        priceText
        priceTitle
        frequency
        isJusAIOffer
        frequencyPeriodText
        priceAfterPromotionalPeriodText
      }
      submitData {
        submitUrl
        jwt
      }
      ...BenefitsList_offerLockCard
      ...CheckoutButton_offerLockCard
      ...OneClickButton_offerLockCard
    }
    ${BenefitsList.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
    ${OneClickButton.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment JurisThreeOffersPlanCard_viewer on Profile {
      pid
      ...OneClickButton_viewer
    }
    ${OneClickButton.fragments.viewer}
  `,
}
